import { AnyEventObject, assign, createMachine, raise } from 'xstate';
import { flow, pipe } from 'fp-ts/function';
import { isNone } from 'fp-ts/Option';
import { OperationVariables } from '@apollo/client';
import { ApiResponse } from 'services/api';

import { NBAContextState, CampaignData, NBADataResponse } from 'modules/ai/nba/types';
import { rejectGraphqlError } from 'modules/ai/utils';
import { defaultState } from 'modules/ai/nba/context/NBAContext';
import { parseAsCampaignsVars, transformToCampaignsQueryData } from 'modules/ai/hooks';
import { transformToNBAQuery } from 'modules/ai/nba/hooks';
import { createLazyGraphqlResultTransform, FetchFn } from 'utils/hooks/useLazyGraphql';
import { getCampaignIds, getTemplateIds, parseTemplatesData } from 'modules/ai/nba/utils/context';
import { CloneAndUpdateStates } from '../hooks/useCreateCloneCampaignAndTemplate.state';

export const NBAContextStates = {
  inactive: 'inactive',
  queryNBAAI: 'queryNBAAI',
  querySupportCampaigns: 'querySupportingCampaigns',
  queryTemplatePreview: 'queryTemplatePreview',
  campaignNotSupported: 'campaignNotSupported',
  ready: 'ready',
  error: 'error',
};

export type NBAContextStateMachineContext = {
  campaignIds: number[];
  nbaData: NBAContextState['nbaData'];
  campaignsData: CampaignData[];
  templatesData: NBAContextState['templatesData'];
  templateIds: number[];
};

export const nbaContextStateMachinePlan = {
  predictableActionArguments: true,
  id: 'nbaContextStateMachine',
  initial: NBAContextStates.inactive,
  context: {
    campaignIds: [],
    campaignsData: [],
    templateIds: [],
    nbaData: defaultState.nbaData,
    templatesData: defaultState.templatesData,
  },
  states: {
    [NBAContextStates.inactive]: {
      on: {
        [NBAContextStates.queryNBAAI]: [
          {
            cond: 'flagsAreEnabled',
            target: NBAContextStates.queryNBAAI,
          },
          {
            target: NBAContextStates.campaignNotSupported,
          },
        ],
      },
    },
    [NBAContextStates.error]: {},
    [NBAContextStates.ready]: {},
    [NBAContextStates.campaignNotSupported]: {},
    [NBAContextStates.queryNBAAI]: {
      invoke: {
        src: 'queryNBAAI',
        onDone: {
          target: NBAContextStates.querySupportCampaigns,
          actions: 'handleNBAAIData',
        },
        onError: {
          target: NBAContextStates.error,
        },
      },
    },
    [NBAContextStates.querySupportCampaigns]: {
      invoke: {
        src: 'queryCampaigns',
        onDone: {
          target: NBAContextStates.queryTemplatePreview,
          actions: 'handleCampaignsData',
        },
        onError: {
          target: NBAContextStates.error,
        },
      },
    },
    [NBAContextStates.queryTemplatePreview]: {
      invoke: {
        src: 'queryTemplatePreviews',
        onDone: {
          actions: 'handleTemplatePreviewsData',
          target: NBAContextStates.ready,
        },
      },
    },
  },
};

export const nbaContextStateMachine = createMachine<NBAContextStateMachineContext>(
  nbaContextStateMachinePlan,
);

export type nbaContextStateMachineEffects = {
  fetchCampaigns: FetchFn<CampaignData, OperationVariables>;
  fetchNBAAI: FetchFn<NBADataResponse, OperationVariables>;
  fetchTemplatePreviews: (args: any) => Promise<ApiResponse<any>>;
  isCampaignsV1Enabled: boolean;
  isNBAEnabled: boolean;
};

export const createnbaContextStateMachineEffects = ({
  fetchCampaigns,
  fetchNBAAI,
  fetchTemplatePreviews,
  isCampaignsV1Enabled,
  isNBAEnabled,
}: nbaContextStateMachineEffects) => ({
  actions: {
    handleNBAAIData: assign((_: NBAContextStateMachineContext, event: AnyEventObject) => {
      const parsedNbaResult = pipe(
        event.data,
        createLazyGraphqlResultTransform(fetchNBAAI),
        transformToNBAQuery,
      );
      if (isNone(parsedNbaResult.data)) {
        raise(CloneAndUpdateStates.error);
        return _;
      }

      return {
        nbaData: parsedNbaResult.data,
        campaignIds: getCampaignIds(parsedNbaResult.data),
      };
    }),
    handleCampaignsData: assign((context: NBAContextStateMachineContext, event: AnyEventObject) => {
      const parsedCampaignsResult = pipe(
        event.data,
        createLazyGraphqlResultTransform(fetchCampaigns),
        transformToCampaignsQueryData,
      );
      return {
        ...context,
        templateIds: getTemplateIds(parsedCampaignsResult.data),
        campaignsData: parsedCampaignsResult.data,
      };
    }),
    handleTemplatePreviewsData: assign(
      (context: NBAContextStateMachineContext, event: AnyEventObject) => {
        return {
          ...context,
          templatesData: parseTemplatesData(event.data),
        };
      },
    ),
  },
  guards: {
    flagsAreEnabled: () => isNBAEnabled && isCampaignsV1Enabled,
  },
  services: {
    queryNBAAI: () => flow(fetchNBAAI, rejectGraphqlError),
    queryCampaigns: (context: NBAContextStateMachineContext) =>
      flow(() => fetchCampaigns(parseAsCampaignsVars(context.campaignIds)), rejectGraphqlError),
    queryTemplatePreviews: (context: NBAContextStateMachineContext) =>
      fetchTemplatePreviews({ templateIds: context.templateIds }),
  },
});
